/**
 * Client-side validation utilities - matches backend Pydantic schemas.
 */

// Email: RFC 5322 simplified (standard best practice)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// BusinessCreateDTO / BusinessUpdateDTO
const PHONE_NUMBER_ID_REGEX = /^\d+$/;  // digits only
const BUSINESS_NAME_MIN = 2;
const BUSINESS_NAME_MAX = 50;
const PHONE_NUMBER_ID_MIN = 10;
const PHONE_NUMBER_ID_MAX = 30;
const SYSTEM_PROMPT_MAX = 2000;
const DESCRIPTION_MAX = 1000;
const HOURS_MAX = 500;
const LOCATION_MAX = 200;

// ServiceItem
const SERVICE_NAME_MIN = 1;
const SERVICE_NAME_MAX = 200;
const SERVICE_PRICE_MAX = 50;
const SERVICE_DURATION_MIN = 15;
const SERVICE_DURATION_MAX = 480;

// Auth
const PASSWORD_MIN = 6;

// Beta landing - Israeli mobile: 05 + 8 digits = 10 total
const BETA_PHONE_REGEX = /^05\d{8}$/;

export type ValidationResult = { valid: true } | { valid: false; message: string };

export function validateEmail(value: string): ValidationResult {
  if (!value || !value.trim()) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (!EMAIL_REGEX.test(value.trim())) {
    return { valid: false, message: 'נא להזין כתובת אימייל תקינה' };
  }
  return { valid: true };
}

export function validatePassword(value: string): ValidationResult {
  if (!value) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (value.length < PASSWORD_MIN) {
    return { valid: false, message: `הסיסמה חייבת להכיל לפחות ${PASSWORD_MIN} תווים` };
  }
  return { valid: true };
}

export function validateConfirmPassword(password: string, confirm: string): ValidationResult {
  if (!confirm) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (password !== confirm) {
    return { valid: false, message: 'הסיסמאות לא תואמות' };
  }
  return { valid: true };
}

export function validateBusinessName(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (trimmed.length < BUSINESS_NAME_MIN) {
    return { valid: false, message: `שם העסק חייב להכיל לפחות ${BUSINESS_NAME_MIN} תווים` };
  }
  if (trimmed.length > BUSINESS_NAME_MAX) {
    return { valid: false, message: `שם העסק לא יכול לעלות על ${BUSINESS_NAME_MAX} תווים` };
  }
  if (/<script>/i.test(trimmed)) {
    return { valid: false, message: 'תווים לא חוקיים' };
  }
  return { valid: true };
}

export function validatePhoneNumberId(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (!PHONE_NUMBER_ID_REGEX.test(trimmed)) {
    return { valid: false, message: 'רק ספרות (ללא רווחים, מקפים או תווים אחרים)' };
  }
  if (trimmed.length < PHONE_NUMBER_ID_MIN) {
    return { valid: false, message: `מינימום ${PHONE_NUMBER_ID_MIN} ספרות` };
  }
  if (trimmed.length > PHONE_NUMBER_ID_MAX) {
    return { valid: false, message: `מקסימום ${PHONE_NUMBER_ID_MAX} ספרות` };
  }
  return { valid: true };
}

export function validateSystemPrompt(value: string, required = true): ValidationResult {
  const trimmed = value.trim();
  if (required && !trimmed) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (trimmed.length > SYSTEM_PROMPT_MAX) {
    return { valid: false, message: `מקסימום ${SYSTEM_PROMPT_MAX} תווים` };
  }
  if (/<script>/i.test(trimmed)) {
    return { valid: false, message: 'תווים לא חוקיים' };
  }
  return { valid: true };
}

export function validateDescription(value: string): ValidationResult {
  if (!value) return { valid: true };
  if (value.length > DESCRIPTION_MAX) {
    return { valid: false, message: `מקסימום ${DESCRIPTION_MAX} תווים` };
  }
  return { valid: true };
}

export function validateHours(value: string): ValidationResult {
  if (!value) return { valid: true };
  if (value.length > HOURS_MAX) {
    return { valid: false, message: `מקסימום ${HOURS_MAX} תווים` };
  }
  return { valid: true };
}

export function validateSystemPromptOptional(value: string): ValidationResult {
  return validateSystemPrompt(value, false);
}

export function validateLocation(value: string): ValidationResult {
  if (!value) return { valid: true };
  if (value.length > LOCATION_MAX) {
    return { valid: false, message: `מקסימום ${LOCATION_MAX} תווים` };
  }
  return { valid: true };
}

export function validateServiceName(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: 'שם השירות חובה' };
  }
  if (trimmed.length < SERVICE_NAME_MIN) {
    return { valid: false, message: 'שם השירות חובה' };
  }
  if (trimmed.length > SERVICE_NAME_MAX) {
    return { valid: false, message: `מקסימום ${SERVICE_NAME_MAX} תווים` };
  }
  return { valid: true };
}

export function validateServicePrice(value: string): ValidationResult {
  if (!value) return { valid: true };
  if (value.length > SERVICE_PRICE_MAX) {
    return { valid: false, message: `מקסימום ${SERVICE_PRICE_MAX} תווים` };
  }
  return { valid: true };
}

/** Normalize phone to 05XXXXXXXX for storage */
export function normalizeBetaPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return value;
  if (digits.startsWith('972') && digits.length >= 12) return '0' + digits.slice(3, 12);
  if (digits.startsWith('5') && digits.length === 9) return '0' + digits;
  if (digits.startsWith('05') && digits.length === 10) return digits;
  return digits.startsWith('0') ? digits.slice(0, 10) : '0' + digits.slice(0, 9);
}

export function validateBetaPhone(value: string): ValidationResult {
  const digits = value.replace(/\D/g, '');
  const normalized = digits.startsWith('972') ? '0' + digits.slice(3) : digits.startsWith('5') && digits.length === 9 ? '0' + digits : digits.startsWith('05') ? digits : '0' + digits;
  if (!normalized || normalized.length !== 10) {
    return { valid: false, message: 'מספר נייד לא תקין (חייב להתחיל ב-05 ולהכיל 10 ספרות)' };
  }
  if (!BETA_PHONE_REGEX.test(normalized)) {
    return { valid: false, message: 'מספר נייד לא תקין (חייב להתחיל ב-05 ולהכיל 10 ספרות)' };
  }
  return { valid: true };
}

export function validateBetaEmail(value: string): ValidationResult {
  if (!value || !value.trim()) return { valid: true }; // optional
  if (!EMAIL_REGEX.test(value.trim())) {
    return { valid: false, message: 'כתובת אימייל לא תקינה' };
  }
  return { valid: true };
}

export function validateBetaFullName(value: string): ValidationResult {
  if (!value || !value.trim()) {
    return { valid: false, message: 'שדה חובה' };
  }
  if (value.trim().length < 2) {
    return { valid: false, message: 'שם חייב להכיל לפחות 2 תווים' };
  }
  return { valid: true };
}

export function validateServiceDuration(value: number): ValidationResult {
  if (value < SERVICE_DURATION_MIN || value > SERVICE_DURATION_MAX) {
    return {
      valid: false,
      message: `משך: בין ${SERVICE_DURATION_MIN} ל-${SERVICE_DURATION_MAX} דקות`,
    };
  }
  return { valid: true };
}
